import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import Slider from 'react-slick';
import { useTranslation } from 'react-i18next';
import { breakpoint, checkDevice } from 'helpers';
import useAuth from 'hooks/useAuth';
import IconConfigWithTheme from 'components/common/icon-config-with-theme';
import { useThemeWithPartner } from 'components/common/ThemeConfigProvider';
import { useNavigate } from 'react-router-dom';
import { HomeServiceBox, HomeServiceWrapper } from './Home.styled';
import { useServiceData } from './homeServiceData';
import type { TServiceWithTheme } from './homeServiceData';
import ReadmoreHomeContent from './ReadmoreHomeContent';
import { useFetcher } from 'hooks/useFetcher';
import useQueryParams from 'hooks/useQueryParams';
import EXPPopupJs from 'components/common/exp-popupjs';

const homeServiceSlideSetting = {
    arrows: false,
    dots: true,
    infinite: false,
    speed: 500,
    slidesToShow: 4,
    slidesToScroll: 1,
    responsive: [
        {
            breakpoint: 1680,
            settings: {
                slidesToShow: 3,
                slidesToScroll: 1,
                infinite: false,
                dots: true,
            },
        },
        {
            breakpoint: 1080,
            settings: {
                slidesToShow: 2,
                slidesToScroll: 1,
                infinite: false,
                dots: true,
            },
        },
        {
            breakpoint: 767,
            settings: {
                slidesToShow: 1,
                slidesToScroll: 1,
                infinite: false,
                centerMode: true,
                dots: true,
            },
        },
    ],
};
const getServiceWithTheme = (themeName: string) => {
    if (themeName === 'rabbit') return ['telemed'];
    if (themeName === 'betterperiod') return ['telepharmacy'];
    if (themeName === 'benix') return ['telepharmacy', 'tlm'];
    if (themeName === 'doctorhub') return ['telepharmacy', 'tlm'];
    if (themeName === 'central') return ['telepharmacy', 'tlm', 'mth', 'nearby'];
    if (themeName === 'central20') return ['tlm', 'fulfillment', 'tlmNcd'];
    if (themeName === 'central30') return ['telepharmacy', 'tlm'];
    if (themeName === 'humatrix') return ['tlm', 'telepharmacy', 'nearby', 'pt', 'nt'];
    if (themeName === 'workplaze') return ['tlm', 'telepharmacy', 'nearby', 'pt', 'nt'];
    if (themeName === 'ptg') return ['tlm', 'telepharmacy'];
    if (themeName === 'default_humatrix') return ['tlm', 'telepharmacy', 'pt', 'nt'];
    if (themeName === 'default_oceanopd') return ['telepharmacy', 'tlm'];
    if (themeName === 'default_sunday') return ['tlm'];
    if (themeName === 'yaphrom') return ['telepharmacy', 'tlm'];
    if (themeName === 'partner_pharmacy') return ['telepharmacy'];
    if (themeName === 'pandf') return ['telepharmacy'];
    if (themeName === 'morningmind') return ['mth'];
    if (themeName === 'no_config') return ['tlm', 'telepharmacy'];
    if (themeName === 'cpa') return ['telepharmacy', 'telepharmacyCPA'];
    if (themeName === 'fastcare') return ['telepharmacy', 'tlm', 'nearby'];
    if (themeName === 'default_pharmcareads') return ['telepharmacyLTC', 'telepharmacy', 'tlm', 'nearby', 'pt', 'nt'];

    return ['telepharmacy', 'tlm', 'tlmNcd', 'mth', 'fulfillment', 'nearby', 'fda', 'pt', 'nt'];
};

function HomeService({ showTitle = false }) {
    const { t } = useTranslation();
    const { auth, profile } = useAuth();
    const navigate = useNavigate();
    const serviceData = useServiceData();
    const theme = useThemeWithPartner();
    const { POST } = useFetcher();
    const { query } = useQueryParams();

    //

    const onRedirect = (data: TServiceWithTheme, event: any, onCheck: boolean) => {
        event?.preventDefault();
        if (theme.name === 'central20' && onCheck) {
            getAllowService(data, event);
        } else if (data?.openExternalBrowser) {
            gotoExternalBrowser(data?.redirectUrl);
        } else if (data?.withParams) {
            if (data?.redirectUrl.includes('?')) {
                const _paramsSearch = window.location.search.replace('?', '&');
                navigate(data?.redirectUrl + _paramsSearch);
            } else {
                navigate(data?.redirectUrl + window.location.search);
            }
        } else if (data?.withPharmacyCode) {
            navigate(data?.redirectUrl + '&pharmacyCode=' + profile?.userXPartner?.partnerPharmacyCode);
        } else {
            navigate(data?.redirectUrl);
        }
    };

    const gotoExternalBrowser = (path: string) => {
        const authorization = auth.accessToken || '';
        const { isWebview, redirectUrl } = checkDevice(path, '', profile);

        if (isWebview) {
            if (authorization) {
                window.open(redirectUrl);
            } else {
                window.open(process.env.REACT_APP_WEB_URL + path + '?openExternalBrowser=1');
            }

            navigate('/');
        } else {
            navigate(path);
        }
    };

    const getAllowService = async (data: TServiceWithTheme, event: any) => {
        try {
            const res = await POST('/user/allowService', {
                serviceType: data.serviceName === 'fulfillment' ? 'FUL' : 'TLM',
                ref: query.ref,
                emp: query.emp,
                issuedate: query.issuedate,
            });
            if (res.statusCode === 200) {
                onRedirect(data, event, false);
            } else if (res.statusCode === 300) {
                EXPPopupJs.simple('warning', t('common:warning'), t('common:warning_ful'));
            }
        } catch (error: any) {
            if (error.statusCode === 400) {
                EXPPopupJs.simple(
                    'warning',
                    t('common:error'),
                    'ลิ้งค์ของท่านหมดอายุหรือไม่ถูกต้อง กรุณาเข้ารับบริการผ่านลิ้งค์จากอีเมลที่ได้รับผ่าน CNEXT'
                );
            } else if (error.isNoPopup) {
                EXPPopupJs.simple('warning', t('common:error'), error.rejectReason);
            }
            if (error.statusCode === 404) {
                console.warn('/user/allowService --> error', error);
            } else {
                console.error('/user/allowService --> error', error);
            }
        }
    };

    return (
        <HomeServiceWrapper>
            {!showTitle && (
                <>
                    {/* <div className='home-service-title' data-test-id='title'>
                    {t('home:our_services')}
                </div> */}

                    <h2 className='home-service-title'>ปรึกษาบุคลากรทางการแพทย์ออนไลน์ตอนนี้</h2>
                    <h2 className='home-service-title'>แนะนำบริการของ PharmCare</h2>
                    <ReadmoreHomeContent>
                        <p className='home-service-detail'>
                            PharmCare คือแพลตฟอร์มเครือข่ายร้านขายยาออนไลน์ ซึ่งเป็นทางเลือกในการดูแลสุขภาพแบบเข้าถึงง่ายและรวดเร็ว
                            เพราะคุณสามารถปรึกษาเภสัชกรออนไลน์ได้ทันทีผ่านแชต โทร หรือ วิดีโอคอล ไม่ว่าจะเป็นอาการเจ็บป่วยเล็กน้อย
                            คำถามเกี่ยวกับการใช้ยา หรือข้อสงสัยด้านสุขภาพอื่น ๆ ทีมเภสัชกรร้านยาของเรา พร้อมให้คำแนะนำที่เหมาะสมเฉพาะบุคคล
                        </p>
                        <p className='home-service-detail'>
                            เราให้บริการครอบคลุมทั้งยาสามัญประจำบ้าน ยาที่จำหน่ายในร้านยาโดยเภสัชกร ยาตามใบสั่งแพทย์ และผลิตภัณฑ์เสริมอาหาร
                            โดยทุกรายการผ่านการดูแลจากทีมเภสัชกร เพื่อประสิทธิภาพในการรักษาอาการเจ็บป่วยของแต่ละบุคคล
                            สามารถสั่งยาออนไลน์ได้เลย พร้อมมีบริการส่ง Delivery ให้ถึงหน้าบ้านของคุณ
                        </p>
                        <p className='home-service-detail'>
                            นอกจากนี้ ยังมีบริการทางการแพทย์ออนไลน์อีกมากมาย ไม่ว่าจะเป็นการปรึกษาแพทย์ ปรึกษาจิตแพทย์
                            รวมถึงค้นหาร้านขายยาใกล้ฉัน สนใจใช้บริการรูปแบบใด อ่านรายละเอียดเพิ่มเติมจากทางด้านล่างนี้ได้เลย
                        </p>
                    </ReadmoreHomeContent>
                </>
            )}
            {serviceData.length > 0 && (
                <div className='home-service-list'>
                    <Slider {...homeServiceSlideSetting}>
                        {serviceData.map((item, index) => (
                            <div data-test-id={item.serviceId} className='h-100' key={index}>
                                <HomeServiceBox backgroundColor={item.backgroundColor}>
                                    <a
                                        href={item.redirectUrl}
                                        className='home-service-box-container h-100'
                                        onClick={(e) => onRedirect(item, e, true)}
                                    >
                                        <div className='home-service-box-title' data-test-id='title'>
                                            {item.titleIcon.type === 'icon' ? (
                                                <IconConfigWithTheme
                                                    pageName={item.titleIcon.pageName as string}
                                                    iconName={item.titleIcon.iconName as string}
                                                />
                                            ) : (
                                                <img {...item.titleIcon.imageOptions} />
                                            )}{' '}
                                            {item.title}
                                        </div>
                                        <div className='home-service-box-content'>
                                            <div className='home-service-box-detail'>
                                                {item.detail}
                                                <div className='-text-subDetail'>{item.subDetail}</div>
                                            </div>
                                            <div className='home-service-box-img'>
                                                <img loading='eager' src={item.imgUrl} alt={item.alt} title={item.alt} />
                                            </div>
                                        </div>
                                        {item.footer && (
                                            <div className='home-service-box-goto'>
                                                <span>{item.footer}</span>&nbsp;
                                            </div>
                                        )}
                                    </a>
                                </HomeServiceBox>
                            </div>
                        ))}
                    </Slider>
                </div>
            )}
        </HomeServiceWrapper>
    );
}

export default HomeService;
